import React, {Component} from "react";
import {LunaCard as LunaCardComponent} from "../../components/Anywhere/LunaCard";
import {profilePlatformTypes} from "../../data/entities/Connect/ProfileEntity";
import BetaManagerService from "../../data/services/BetaManagerService";
import {UserContext} from "../../context/UserContext";
import {AdobeAnalyticsContextData} from "../../context/AdobeAnalyticsContext";
import {scrollToElement} from "../../utils/browser";

interface Props {
    analyticsContext: AdobeAnalyticsContextData;
}

interface State {
    error: boolean;
    loading: boolean;
    pendingUserApproval: boolean;
    betaUserApproved: boolean;
    lunaAccountLinked: boolean;
}

export default class LunaCard extends Component<Props, State> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    state: State = {
        error: false,
        loading: true,
        pendingUserApproval: false,
        betaUserApproved: false,
        lunaAccountLinked: false,
    };

    componentDidMount() {
        this.checkBetaAccess();
    }

    render(): JSX.Element {
        return (
            <LunaCardComponent
                error={this.state.error}
                onRefresh={this.checkBetaAccess}
                loading={this.state.loading}
                pendingUserApproval={this.state.pendingUserApproval}
                onCodeRequest={this.requestBetaAccess}
                onSurveyAccess={this.accessSurvey}
                onSurveySkip={this.skipSurvey}
                onLunaAccess={this.accessLuna}
                betaUserApproved={this.state.betaUserApproved}
                accountLinked={this.state.lunaAccountLinked}
            />
        );
    }

    checkBetaAccess = () => {
        this.setState({loading: true}, async () => {
            let userApproved: boolean = false;
            let accountLinked: boolean = false;
            let error: boolean = false;

            let lunaUserProfile = this.context.user.profiles.find(profile => (
                profile.platformType === profilePlatformTypes.amazonstream
            ));

            if (lunaUserProfile) {
                accountLinked = true;
            } else {
                if (
                    this.context.user.ticketData.ticket &&
                    this.context.user.ticketData.sessionId &&
                    this.context.user.ticketData.userId &&
                    process.env.REACT_APP_BETA_MANAGER_LUNA_PHASE_ID &&
                    process.env.REACT_APP_BETA_MANAGER_LUNA_PLAYER_GROUP_ID
                ) {
                    try {
                        const betaResponse = await BetaManagerService.getPlayerProfile(
                            this.context.user.ticketData.ticket,
                            this.context.user.ticketData.sessionId,
                            this.context.user.ticketData.userId,
                            "STO",
                            parseInt(process.env.REACT_APP_BETA_MANAGER_LUNA_PHASE_ID)
                        )

                        const lunaPlayerGroup = betaResponse.data.PlayerGroups.find(playerGroup => (
                            playerGroup.PlayerGroupId === parseInt(process.env.REACT_APP_BETA_MANAGER_LUNA_PLAYER_GROUP_ID as string)
                        ));

                        if (lunaPlayerGroup && lunaPlayerGroup.IsApproved) {
                            userApproved = true;
                        }
                    } catch (err) {
                        console.log("Unable to fetch Beta Manager player profile data");
                        error = true;
                    }
                }
            }

            this.setState({
                loading: false,
                error: error,
                betaUserApproved: userApproved,
                lunaAccountLinked: accountLinked,
            });
        })
    };

    requestBetaAccess = (): void => {
        this.props.analyticsContext.track("clickevent", {
            location: "page",
            category: "interaction",
            action: "Luna : request code",
        });

        this.setState({pendingUserApproval: true}, () => {
            if (
                this.context.user.ticketData.ticket &&
                this.context.user.ticketData.sessionId &&
                this.context.user.ticketData.userId &&
                process.env.REACT_APP_BETA_MANAGER_LUNA_PHASE_ID &&
                process.env.REACT_APP_BETA_MANAGER_LUNA_PLAYER_GROUP_ID
            ) {
                BetaManagerService.registerPlayer(
                    this.context.user.ticketData.ticket,
                    this.context.user.ticketData.sessionId,
                    this.context.user.ticketData.userId,
                    "STO",
                    parseInt(process.env.REACT_APP_BETA_MANAGER_LUNA_PHASE_ID),
                    parseInt(process.env.REACT_APP_BETA_MANAGER_LUNA_PLAYER_GROUP_ID),
                    "vault"
                ).then(() => {
                    this.setState({pendingUserApproval: false, betaUserApproved: true});
                }).catch(() => {
                    console.log("Unable to register user with Beta Manager");
                    this.setState({pendingUserApproval: false, betaUserApproved: false, error: true});
                })
            }
        })
    };

    accessSurvey = (): void => {
        this.props.analyticsContext.track("clickevent", {
            location: "page",
            category: "interaction",
            action: "Luna : access to survey",
        });

        window.open("https://www.amazon.com/luna/invite/survey", "_blank");
    };

    skipSurvey = (): void => {
        this.props.analyticsContext.track("clickevent", {
            location: "page",
            category: "interaction",
            action: "Luna : I have already filled the survey",
        });

        scrollToElement("faq-section");
    };

    accessLuna = (): void => {
        this.props.analyticsContext.track("clickevent", {
            location: "page",
            category: "interaction",
            action: "Luna : play now on Luna",
        });

        window.open("https://www.amazon.com/luna/","_blank");
    };
}